/* eslint-disable no-restricted-globals */
/* global cacmLocalVars */

/* eslint-disable class-methods-use-this */

// File for logic corresponding to AuthCommentForm component
import { Component } from 'js-component-framework';
import './index.scss';
import { __, sprintf } from '@wordpress/i18n';
import DOMPurify from 'dompurify';
import { isUserLoggedIn } from '../../js/utils/membership';

/**
 * Component for Auth Comment Form
 */
export default class AuthCommentForm extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);
    if (isUserLoggedIn()) {
      this.element.classList.add('auth-comment-form--is-logged-in');
      this.init();
    } else {
      this.element.classList.add('auth-comment-form--not-authorized');
    }
  }

  /**
   * Initialize Auth Comment Form.
   * @returns {Promise<void>}
   */
  async init() {
    const { contents } = this.children;
    contents.innerHTML = await this.buildForm();
    this.maybeUpdateReplyCom();
    this.element.addEventListener('submit', this.submitForm);
  }

  /**
   * Build the contact form from REST API endpoint.
   * @returns {Promise<string | string>}
   */
  async buildForm() {
    return fetch(cacmLocalVars.restCommentFormUrl ?? '')
      .then((response) => {
        if (response && response.status === 200 && response.ok) {
          return response.text();
        }
        // Error building form or unauthorized user.
        throw new Error();
      })
      .then((html) => html)
      .catch(() => '');
  }

  /**
   * Handle submit form event.
   * @param {object} event the event object.
   */
  submitForm(event) {
    event.preventDefault();

    const {
      author = { value: '' },
      email = { value: '' },
      comment = { value: '' },
      comment_post_ID: postId = { value: '' },
      comment_parent: replyPostId = { value: 0 },
      _wpnonce: nonce = { value: '' },
    } = event?.target?.elements;

    const data = JSON.stringify({
      content: comment.value,
      post: postId.value,
      author_name: author.value,
      author_email: email.value,
      parent: replyPostId.value,
      _wpnonce: nonce.value,
    });

    fetch(
      cacmLocalVars.restCommentsUrl ?? '',
      {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      },
    )
      .then((response) => {
        if (response.ok) {
          // We know response came back successfully, act on response early here...
          // eslint-disable-next-line no-alert
          alert(__(`Thanks for your comment, ${author.value}!\n Per our policy, we review every comment before it is posted.`, 'cacm'));
        } else {
          // Something went wrong with response.. log to console for debugging.
          console.log(response);
        }

        return response.json(); // always return response JSON.
      })
      .then((object) => {
        if (object.message) {
          // Wordpress will return errors if comment is flaged for some reason.
          // eslint-disable-next-line no-alert
          alert(object.message);
          return;
        }
        // Reload page.
        // eslint-disable-next-line no-restricted-globals
        location.reload();
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  /**
   * Maybe update for commenting on another comment.
   * @returns void
   */
  maybeUpdateReplyCom() {
    const { replytocom = '0' } = this.element.dataset;

    if (replytocom === '0') {
      return;
    }

    const commentParent = this.element.querySelector('#comment_parent');
    const submitButton = this.element.querySelector('#submit');

    if (commentParent && commentParent.value) {
      commentParent.value = replytocom;
    }

    if (submitButton && submitButton.value) {
      submitButton.value = __('Reply to Comment', 'cacm');
    }

    // Construct the reply title with link and allow user to cancel reply comment.
    const replyTitle = this.element.querySelector('#reply-title');
    const replyToCommentElement = document.querySelector(`#comment-${replytocom}`);
    let replyToAuthor = null;

    if (replyToCommentElement) {
      replyToAuthor = replyToCommentElement.querySelector('.comment-author > .fn').innerText ?? '';
    }

    if (replyTitle && replyToAuthor) {
      const cancelLink = sprintf(
        '<small><a rel="nofollow" id="cancel-comment-reply-link" href="%s#respond">%s</a></small>',
        location.pathname,
        __('Cancel reply', 'cacm'),
      );

      replyTitle.innerHTML = DOMPurify.sanitize(
        sprintf(
          __('Leave a Reply to %s %s', 'cacm'),
          `<a href="#comment-${replytocom}">${replyToAuthor}</a>`,
          cancelLink,
        ),
      );
    }
  }
}
